import { CompanyInformationFields } from './company-information-fields'
import {
  FormalNameFields,
  StudentAddressFields,
} from '../common/identity-fields.js'
/**
 * Create-student admin screen.
 *
 * Cooori-internal admin only (the whole admin SPA is gated to
 * admin by RoleGate; the API re-checks the role). An
 * operator tool for standing up a login-ready test client without the
 * db.seed.users CLI. English-only by design: internal tooling, never a
 * student-facing surface, so it does not participate in lesson-content review.
 *
 * zod + React Hook Form since ADMIN-FORMS: the schema in
 * `student-schema.ts` owns the field rules; this page owns everything
 * that needs server state (the ended-cohort blocker, the live email
 * check, and cohort scheduling).
 */
import { useEffect, useRef, useState } from 'react'
import { Controller, useController, useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { keysStaleAfterStudentChange, queryKeys } from '../api/query-keys'
import { Link, useSearchParams } from 'react-router-dom'
import { useResetOnChange } from '../common/use-reset-on-change'
import { PageHeader } from '../components/page-header'
import { OPERATOR_FORM_PAGE, OPERATOR_PAGE_MAIN } from '../common/page-layout'
import { ProvisioningFormActions } from '../common/provisioning-form-actions'
import { ClientPicker } from '../clients/client-picker'
import { useClient } from '../clients/use-client'
import {
  AccessWindowCell,
  AccessWindowPanel,
  formatBusinessDate,
  formatWindowLength,
  todayJstIso,
} from '../common/access-window'
import { CohortPicker } from '../cohorts/cohort-picker'
import { cohortEnrollmentReadinessProblems } from '../cohorts/enrollment-readiness'
import { useCohort } from '../cohorts/use-cohort'
import {
  PrimaryButton,
  SecondaryButton,
  useSubmitIntent,
} from '../common/submit-pair'
import {
  UnsavedChangesGuard,
  useLeaveAfterSave,
} from '../common/unsaved-changes'
import { FormError, formMessage, messageFor, resolveFailure, type ResolvedFailure } from '../common/field-errors'
import { DEFAULT_TROPHY_GOAL } from '../common/product-defaults'
import {
  ProgramPicker,
  usePrograms,
} from '../common/programs'
import { Button, FieldError, FieldLabel, Input, Notice, Select, SelectContent, SelectItem, SelectTrigger, SelectValue, useToast } from '@speaking-coach/ui'
import {
  STUDENT_ERROR_FIELD,
  adminStudentClient,
  messageForStudentError,
  type StudentField,
} from './api'
import {
  createStudentSchema,
  type CreateStudentForm,
} from './student-schema'
import { CohortEnrollmentDefaultsPreview } from './cohort-enrollment-defaults-preview'
import { InvitationScheduleSummary } from './invitation-schedule-summary'
import type { ProductTier } from './types'

const TIERS: ProductTier[] = ['essential', 'pro', 'premium', 'unlimited']

/** Long enough that typing an address does not fire a request per keystroke. */
const EMAIL_CHECK_DEBOUNCE_MS = 400

export function CreateStudentPage(): JSX.Element {
  // Success navigation goes through `leave` so the unsaved-changes guard is
  // provably stood down first — see `useLeaveAfterSave`.
  const { leaving, leave } = useLeaveAfterSave()
  const { toast } = useToast()
  // Two endings, two destinations — see `useSubmitIntent`.
  const { intent, primaryProps, anotherProps } = useSubmitIntent()
  const [searchParams] = useSearchParams()
  const businessToday = todayJstIso()

  /**
   * Live validation keeps both create actions aligned with the form. This
   * replaces the old `standaloneStartTouched` / `standaloneEndTouched` /
   * `submitAttempted` bookkeeping and the confusing clickable-invalid state.
   *
   * `?clientId=` carries context in from a client page; otherwise the
   * picker starts UNCHOSEN — same rule as the batch form. This used to
   * fall back to the operator's HOME client, which meant the one field
   * deciding which client receives the student arrived silently
   * pre-answered with Cooori's own tenant.
   */
  const form = useForm<CreateStudentForm>({
    resolver: zodResolver(createStudentSchema(businessToday), undefined, {
      mode: 'sync',
    }),
    mode: 'onChange',
    defaultValues: {
      clientId: searchParams.get('clientId') ?? '',
      ownership: 'company',
      cohortId: '',
      email: '',
      clientEmployeeId: '',
      clientDepartment: '',
      firstName: '',
      lastName: '',
      username: '',
      addressSuffix: 'san',
      tier: 'pro',
      standaloneScheduledStartOn: '',
      standaloneAccessEndsOn: '',
      programId: '',
      trophyGoal: String(DEFAULT_TROPHY_GOAL),
      externalPolicyRef: '',
    },
  })
  const { control, handleSubmit, resetField, setValue, trigger, watch, formState } = form
  // Register even while company ownership hides the private terms, so a
  // loaded client target can update both the value and its clean baseline.
  const { field: trophyGoalField } = useController({ control, name: 'trophyGoal' })
  const errors = formState.errors
  // Subscribe to the complete value set once so the primary actions reflect
  // both schema validity and server-backed enrollment readiness.
  const formValues = watch()
  const {
    clientId,
    ownership,
    cohortId,
    email,
    firstName,
    lastName,
    programId,
    standaloneScheduledStartOn,
    standaloneAccessEndsOn,
  } = formValues
  const programs = usePrograms()
  const privateProgramAvailable =
    ownership !== 'private' ||
    (programs.isSuccess &&
      programs.data.programs.some(
        (program) => program.id === programId && program.readyForEnrollment,
      ))

  // A resolved failure rather than a bare string: the field it belongs to
  // decides WHERE it renders (at the field, or in the summary at the top).
  const [failure, setFailure] = useState<ResolvedFailure<StudentField> | null>(
    null,
  )
  // Only used by the "and another" path: on the primary path we navigate
  // away, so there is nothing to report here.
  const [lastCreated, setLastCreated] = useState<string | null>(null)
  const [keptActivation, setKeptActivation] = useState(false)
  const queryClient = useQueryClient()

  // Same by-id lookup the picker resolves its label from — one hook, so
  // one request and no chance of the two disagreeing about which client
  // is selected. This used to be a hand-copied `{ limit: 200 }` params
  // literal kept byte-identical to the picker's in order to share its
  // cache entry, which stopped being true the moment the picker changed.
  // ClientPicker owns the selection UI; this owns the policy derived
  // from that selection.
  const selectedClientQuery = useClient(clientId)

  // Cohorts belong to one client, so the list follows the client picker.
  //
  // No `joinableOnly` here: an ended or incomplete cohort stays visible but
  // disabled with an explanation. Hiding it would hide the client's setup
  // context precisely when an operator needs to repair it.
  //
  // A cohort failure stays non-fatal — a student with no cohort is a valid
  // state, so the picker degrading must not block creating one.
  // `?? undefined`: the hook answers `null` for "resolved, no such
  // client", and everything below treats unresolved and missing the same
  // way — fail closed.
  const selectedClient = selectedClientQuery.data ?? undefined

  // A private enrollment starts from the selected client's trophy target.
  // Reset when the operator changes client so an override chosen for Acme is
  // never carried into Contoso. If the lookup is slow and the operator edits
  // the field first, preserve that explicit choice when the response arrives.
  const trophyDefaultClientId = useRef(clientId)
  const appliedClientTrophyDefault = useRef<string | null>(null)
  const trophyGoalEditedByOperator = useRef(false)
  useEffect(() => {
    if (trophyDefaultClientId.current === clientId) return
    setValue('clientEmployeeId', '', { shouldDirty: true })
    setValue('clientDepartment', '', { shouldDirty: true })
    trophyDefaultClientId.current = clientId
    appliedClientTrophyDefault.current = null
    trophyGoalEditedByOperator.current = false
    resetField('trophyGoal', {
      defaultValue: String(DEFAULT_TROPHY_GOAL),
    })
  }, [clientId, resetField, setValue])
  useEffect(() => {
    if (selectedClient?.clientId !== clientId) return
    const defaultKey = `${clientId}:${selectedClient.contractTrophyTarget}`
    if (
      appliedClientTrophyDefault.current === defaultKey ||
      trophyGoalEditedByOperator.current
    ) {
      return
    }
    appliedClientTrophyDefault.current = defaultKey
    resetField('trophyGoal', {
      defaultValue: String(selectedClient.contractTrophyTarget),
    })
  }, [clientId, resetField, selectedClient])
  // Resolved by id and UNFILTERED, so an ended cohort still resolves and
  // the refusal below can name its end date.
  const companyOwned = ownership === 'company'
  const selectedCohort = useCohort(
    clientId.trim(),
    companyOwned ? cohortId : '',
  ).data ?? undefined
  const resolvedStartOn =
    companyOwned
      ? (selectedCohort?.scheduledStartOn ?? null)
      : standaloneScheduledStartOn === ''
        ? null
        : standaloneScheduledStartOn
  const resolvedEndOn =
    companyOwned
      ? (selectedCohort?.accessEndsOn ?? null)
      : standaloneAccessEndsOn === ''
        ? null
        : standaloneAccessEndsOn
  const windowLength =
    companyOwned && selectedCohort === undefined
      ? null
      : (formatWindowLength(resolvedStartOn, resolvedEndOn) ?? 'Not fixed')
  // Server state, so NOT in the schema. Every enrollment screen uses the
  // same readiness rules and wording; a missing trophy goal must not hide as
  // a neutral “not set” summary on this page while repeat enrollment blocks.
  const baseSelectedCohortProblems =
    companyOwned && selectedCohort !== undefined
      ? cohortEnrollmentReadinessProblems(selectedCohort, businessToday)
      : []
  const selectedCohortProgramReady =
    selectedCohort !== undefined &&
    programs.isSuccess &&
    programs.data.programs.some(
      (program) =>
        program.id === selectedCohort.defaultProgramId && program.readyForEnrollment,
    )
  const selectedCohortProblems = [
    ...baseSelectedCohortProblems,
    ...(companyOwned &&
    selectedCohort !== undefined &&
    selectedCohort.defaultProgramId.trim() !== '' &&
    programs.isSuccess &&
    !selectedCohortProgramReady
      ? [
          'The cohort program is not ready for enrollment. Its diagnostic, default tutor, and lessons at every supported level must be available.',
        ]
      : []),
    ...(companyOwned && selectedCohort !== undefined && programs.isError
      ? ['Programs could not be verified. Reload before creating the enrollment.']
      : []),
  ]
  const selectedCohortReady =
    selectedCohortProblems.length === 0 &&
    (!companyOwned ||
      selectedCohort === undefined ||
      (programs.isSuccess && selectedCohortProgramReady))
  const noEnrollableCohort =
    companyOwned &&
    cohortId === '' &&
    selectedClient !== undefined &&
    selectedClient.cohortCount > 0 &&
    selectedClient.enrollableCohortCount === 0
  const cohortReadinessMessage =
    selectedCohortProblems.length > 0
      ? selectedCohortProblems.join(' ')
      : noEnrollableCohort
        ? 'This client does not currently have an enrollable cohort. Update an existing cohort or create a new one before enrolling the student, or choose a private enrollment.'
        : null
  // Changing the client clears any prior cohort selection: a cohort id from
  // the previous client would 404, and the composite FK makes it unwritable
  // regardless. Via the shared helper, which also closes a trap this page
  // was only accidentally clear of — the bare effect ran on MOUNT too, and
  // was harmless solely because `cohortId` is not seeded from the query
  // string here. Adding `?cohortId=` deep-linking, the obvious symmetry
  // with the batch page, would have wiped it on first paint.
  useResetOnChange(clientId, () =>
    setValue('cohortId', '', {
      shouldDirty: true,
      shouldValidate: true,
    }),
  )

  // Debounced availability check, so a duplicate is caught while the
  // operator is still in the email field rather than after they submit.
  const [checkedEmail, setCheckedEmail] = useState('')
  useEffect(() => {
    const candidate = email.trim().toLowerCase()
    // Not "is this valid" — just enough to avoid a request per keystroke
    // while someone types the local part.
    if (!candidate.includes('@') || candidate.length < 5) {
      setCheckedEmail('')
      return
    }
    const handle = setTimeout(
      () => setCheckedEmail(candidate),
      EMAIL_CHECK_DEBOUNCE_MS,
    )
    return () => clearTimeout(handle)
  }, [email])

  const emailCheck = useQuery({
    queryKey: queryKeys.studentEmailAvailable(checkedEmail),
    queryFn: () => adminStudentClient.checkEmailAvailable(checkedEmail),
    enabled: checkedEmail !== '',
  })

  /**
   * `null` = not checked (empty, too short, in flight, or the check failed);
   * the server re-checks on submit regardless, so this is advisory.
   *
   * The answer counts only if it answers the address the field holds *now*.
   * The server echoes the email back, and comparing against it is what stops
   * a slow reply for an earlier keystroke reporting on a later address. The
   * query key gives us this a second time over — a stale reply resolves into
   * its own cache entry, not this one — but the echo check stays because it
   * is the server's guarantee rather than our cache's.
   */
  const emailTaken: boolean | null =
    emailCheck.data !== undefined &&
    emailCheck.data.email === email.trim().toLowerCase()
      ? !emailCheck.data.available
      : null
  const canSubmit =
    formState.isValid &&
    createStudentSchema(businessToday).safeParse(formValues).success &&
    clientId.trim() !== '' &&
    email.trim() !== '' &&
    firstName.trim() !== '' &&
    lastName.trim() !== '' &&
    (!companyOwned || cohortId !== '') &&
    selectedCohortReady &&
    privateProgramAvailable &&
    emailTaken !== true

  const create = useMutation({
    mutationFn: (values: CreateStudentForm) => {
      const company = values.ownership === 'company'
      return adminStudentClient.createEnrollment(values.clientId.trim(), {
        cohortId: company ? values.cohortId : null,
        email: values.email.trim(),
        clientEmployeeId: selectedClient?.clientEmployeeIdEnabled ? values.clientEmployeeId : undefined,
        clientDepartment: selectedClient?.clientDepartmentEnabled ? values.clientDepartment : undefined,
        firstName: values.firstName,
        lastName: values.lastName,
        username: values.username,
        addressSuffix: values.addressSuffix,
        tier: company ? null : values.tier,
        scheduledStartOn: company ? null : values.standaloneScheduledStartOn,
        accessEndsOn: company ? null : values.standaloneAccessEndsOn,
        programId: company ? null : values.programId.trim(),
        trophyGoal: company ? null : Number(values.trophyGoal),
        placementRequirement: 'require_new_diagnostic',
        curriculumProgressRequirement: 'start_new_attempt',
        trophyProgressRequirement: 'start_new_progress',
        externalPolicyRef: company
          ? null
          : values.externalPolicyRef.trim() || null,
      })
    },
    onSuccess: (created, values) => {
      for (const key of keysStaleAfterStudentChange(values.clientId.trim())) {
        void queryClient.invalidateQueries({ queryKey: key })
      }
      const kept = created.notificationState === 'existing_activation'
      setKeptActivation(kept)
      if (intent.current === 'close') {
        if (kept) toast({ variant: 'success', description: 'Enrollment created. The existing activation invitation keeps its original send schedule; check invitation details on the student profile.' })
        // Straight to the student we just made: that is where an operator
        // verifies it and where the next action lives.
        leave(`/admin/students/${created.enrollment.userId}/edit`)
        return
      }
      // Stay, and keep the shared context (client, cohort, tier) so
      // enrolling a wave one at a time stays cheap. Only the
      // per-person fields clear — via `reset`, so the form is PRISTINE
      // again and the guard stands down until the next person is typed.
      setLastCreated(values.email.trim())
      form.reset({
        ...form.getValues(),
        email: '',
        clientEmployeeId: '',
        clientDepartment: '',
        firstName: '',
        lastName: '',
        username: '',
        addressSuffix: 'san',
      })
    },
    onError: (err: unknown) => {
      setFailure(
        resolveFailure(err, STUDENT_ERROR_FIELD, messageForStudentError),
      )
    },
  })

  const submitting = create.isPending

  const onValid = (values: CreateStudentForm): void => {
    setFailure(null)
    setLastCreated(null)
    // The button is disabled for this too, but Enter in a text field
    // submits past a disabled button.
    if (!canSubmit || create.isPending) return
    create.mutate(values)
  }

  return (
    <main className={OPERATOR_PAGE_MAIN}>
      <div className={OPERATOR_FORM_PAGE}>
        <PageHeader title="Enroll one student" />
        <UnsavedChangesGuard
          when={formState.isDirty && !leaving}
          name="the new student"
        />

        <section className="bg-surface-card rounded-token-card p-6">
          <form
            onSubmit={handleSubmit(onValid)}
            className="space-y-4"
            aria-label="Enroll one student"
            noValidate
          >
            {/* `noValidate`: the zod schema is the authority, and native
                constraint validation would pre-empt it — the browser's own
                bubble appears instead of our wording, and in jsdom it blocks
                submission outright so `handleSubmit` never runs. The
                `required` attributes stay for assistive tech. */}
            {/* At the TOP: a failure below the fold, under the button, is a
                failure an operator scrolls past. Only shown for errors that
                belong to no field — anything field-specific renders at that
                field instead, so the same text never appears twice. */}
            <FormError message={formMessage(failure)} />

            <Notice variant="neutral">
              <p className="font-medium text-text-primary">
                Returning student?
              </p>
              <p className="mt-0.5">
                Create the new enrollment from their existing student page so
                their sign-in and learning history stay attached.
              </p>
            </Notice>

            <div>
              <FieldLabel
                htmlFor="student-client-id"
                label="Client"
                help="The client this student belongs to. The student is created in whichever client you pick."
              />
              <Controller
                control={control}
                name="clientId"
                render={({ field }) => (
                  <ClientPicker
                    id="student-client-id"
                    label="Client"
                    required
                    value={field.value}
                    onChange={(nextClientId) => {
                      field.onChange(nextClientId)
                      if (nextClientId === '') {
                        setValue('ownership', 'company', {
                          shouldDirty: true,
                          shouldValidate: true,
                        })
                      }
                    }}
                    ariaInvalid={errors.clientId !== undefined}
                    ariaDescribedby={
                      errors.clientId !== undefined
                        ? 'student-client-required'
                        : undefined
                    }
                  />
                )}
              />
              <FieldError
                id="student-client-required"
                message={errors.clientId?.message ?? null}
              />
            </div>

            <div>
              <FieldLabel
                htmlFor="student-ownership"
                label="Enrollment ownership"
                help={
                  <>
                  Company enrollments belong to a cohort and can be visible to
                  its assigned HR managers. Private enrollments remain visible
                  only to the student and Cooori operators.
                  </>
                }
              />
              <Controller
                control={control}
                name="ownership"
                render={({ field }) => (
                  <Select
                    value={field.value}
                    disabled={clientId.trim() === ''}
                    onValueChange={(value: 'company' | 'private') => {
                      field.onChange(value)
                      setValue('cohortId', '', {
                        shouldDirty: true,
                        shouldValidate: true,
                      })
                      if (value === 'private') {
                        // Choosing the exceptional private path makes both
                        // dates immediately decision-relevant. Surface those
                        // blockers beside the fields while the actions are
                        // disabled instead of requiring an impossible submit.
                        void trigger([
                          'standaloneScheduledStartOn',
                          'standaloneAccessEndsOn',
                        ])
                      }
                    }}
                  >
                    <SelectTrigger
                      id="student-ownership"
                      aria-describedby={
                        clientId.trim() === ''
                          ? 'student-ownership-needs-client'
                          : undefined
                      }
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="company">
                        Company — controlled by cohort HR access
                      </SelectItem>
                      <SelectItem value="private">
                        Private — student and Cooori only
                      </SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
              {clientId.trim() === '' && (
                <p
                  id="student-ownership-needs-client"
                  className="mt-1 text-caption text-text-secondary"
                >
                  Choose a client first. Private enrollments still require a
                  client for security and data isolation, but remain hidden
                  from that client&apos;s HR users.
                </p>
              )}
              {!companyOwned && (
                <Notice variant="warning" className="mt-2 py-2" textSize="caption">
                  Cooori does not currently offer self-service enrollment.
                  Confirm that access dates, price, and payment terms were
                  agreed with the student outside this system.
                </Notice>
              )}
            </div>

            {companyOwned && (
              <div>
                <FieldLabel
                  htmlFor="student-cohort-id"
                  label="Cohort"
                  help="Supplies the locked dates, tier, program, trophy goal, trial classification, and company visibility for this enrollment."
                />
                <Controller
                  control={control}
                  name="cohortId"
                  render={({ field }) => (
                    <CohortPicker
                      id="student-cohort-id"
                      label="Cohort"
                      clientId={clientId.trim()}
                      value={field.value}
                      onChange={field.onChange}
                      emptyLabel="Choose a cohort"
                      required
                      requireCompleteEnrollmentDefaults
                      ariaInvalid={
                        cohortReadinessMessage !== null ||
                        errors.cohortId !== undefined ||
                        undefined
                      }
                      ariaDescribedby={
                        cohortReadinessMessage !== null
                          ? 'student-cohort-readiness-error'
                          : undefined
                      }
                    />
                  )}
                />
                <FieldError
                  message={
                    messageFor(failure, 'cohortId') ??
                    errors.cohortId?.message ??
                    null
                  }
                />
                <FieldError
                  id="student-cohort-readiness-error"
                  message={cohortReadinessMessage}
                />
                {selectedClient?.cohortCount === 0 && (
                  <p className="mt-1 text-caption text-text-secondary">
                    This client has no cohorts yet. Create one first, or
                    explicitly choose a private enrollment above.
                  </p>
                )}
              </div>
            )}

            <AccessWindowPanel
              titleId="create-program-access-window-title"
              windowLength={windowLength}
              badge={
                companyOwned
                  ? selectedCohort === undefined
                    ? 'Choose a cohort'
                    : `Cohort: ${selectedCohort.name}`
                  : 'Private enrollment'
              }
              footer={
                <InvitationScheduleSummary
                  source={
                    companyOwned
                      ? selectedCohort === undefined
                        ? { kind: 'unselected' }
                        : { kind: 'cohort', cohort: selectedCohort }
                      : { kind: 'private' }
                  }
                />
              }
            >
              <AccessWindowCell
                label="Access starts"
                htmlFor="create-scheduled-start"
              >
                {!companyOwned ? (
                  <>
                    <Controller
                      control={control}
                      name="standaloneScheduledStartOn"
                      render={({ field }) => (
                        <Input
                          id="create-scheduled-start"
                          type="date"
                          value={field.value}
                          aria-invalid={
                            errors.standaloneScheduledStartOn !== undefined ||
                            messageFor(failure, 'scheduledStartOn') !== null ||
                            undefined
                          }
                          onChange={(event) => {
                            field.onChange(event)
                            void trigger([
                              'standaloneScheduledStartOn',
                              'standaloneAccessEndsOn',
                            ])
                          }}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                    <FieldError
                      message={
                        messageFor(failure, 'scheduledStartOn') ??
                        errors.standaloneScheduledStartOn?.message ??
                        null
                      }
                    />
                  </>
                ) : selectedCohort === undefined ? (
                  'Choose a cohort'
                ) : (
                  formatBusinessDate(resolvedStartOn, 'Not set')
                )}
              </AccessWindowCell>
              <AccessWindowCell label="Access ends" htmlFor="create-access-end">
                {!companyOwned ? (
                  <>
                    <Controller
                      control={control}
                      name="standaloneAccessEndsOn"
                      render={({ field }) => (
                        <Input
                          id="create-access-end"
                          type="date"
                          required
                          value={field.value}
                          aria-invalid={
                            errors.standaloneAccessEndsOn !== undefined ||
                            messageFor(failure, 'accessEndsOn') !== null ||
                            undefined
                          }
                          onChange={(event) => {
                            field.onChange(event)
                            void trigger([
                              'standaloneScheduledStartOn',
                              'standaloneAccessEndsOn',
                            ])
                          }}
                          onBlur={field.onBlur}
                        />
                      )}
                    />
                    <FieldError
                      message={
                        messageFor(failure, 'accessEndsOn') ??
                        errors.standaloneAccessEndsOn?.message ??
                        null
                      }
                    />
                  </>
                ) : selectedCohort === undefined ? (
                  'Choose a cohort'
                ) : (
                  formatBusinessDate(resolvedEndOn)
                )}
              </AccessWindowCell>
            </AccessWindowPanel>

            {companyOwned &&
              selectedCohort !== undefined &&
              selectedCohortReady && (
                <CohortEnrollmentDefaultsPreview
                  cohort={selectedCohort}
                  includeAccessWindow={false}
                />
              )}

            <div>
              <FieldLabel
                htmlFor="student-email"
                label="Email"
                help={
                  <>
                    Email identifies the student account. Create another
                    enrollment from an existing student&apos;s page instead of
                    creating a second account.
                  </>
                }
              />
              <Controller
                control={control}
                name="email"
                render={({ field }) => (
                  <Input
                    id="student-email"
                    type="email"
                    required
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-invalid={
                      emailTaken === true ||
                      errors.email !== undefined ||
                      undefined
                    }
                  />
                )}
              />
              {/* The live check fires while typing; the server's 409 lands on
                  submit. Same field, so whichever knows first speaks — and
                  the pre-check deliberately does not block the submit, since
                  the create route is the authority. */}
              <FieldError
                message={
                  messageFor(failure, 'email') ??
                  errors.email?.message ??
                  (emailTaken === true
                    ? "That email is already registered. Add the enrollment from the existing student's page, or use a different email."
                    : null)
                }
              />
            </div>

            <FormalNameFields
              firstName={form.register('firstName')}
              lastName={form.register('lastName')}
              errors={form.formState.errors}
            />
            <Controller
              control={form.control}
              name="addressSuffix"
              render={({ field }) => (
                <StudentAddressFields
                  username={form.register('username')}
                  required={false}
                  suffix={field.value}
                  onSuffixChange={field.onChange}
                  errors={form.formState.errors}
                />
              )}
            />
            <fieldset className="space-y-3">
              <legend className="font-medium">Company information</legend>
              <p className="text-body text-text-secondary">
                {!clientId ? 'Select a client to see which company fields are available.'
                  : selectedClientQuery.isError ? 'Could not load client settings. Company fields are unavailable until settings load.'
                  : !selectedClient ? (selectedClientQuery.isPending ? 'Loading client settings…' : 'Client settings are unavailable.')
                  : <>Each field is enabled independently in <Link className="underline" to={`/admin/clients/${clientId}`}>client settings</Link>. Disabled fields cannot be entered and are hidden from HR.</>}
              </p>
              {selectedClientQuery.isError && (
                <Button variant="secondary" type="button" onClick={() => void selectedClientQuery.refetch()}>Retry client settings</Button>
              )}
              <CompanyInformationFields
                employee={form.register('clientEmployeeId')}
                department={form.register('clientDepartment')}
                employeeDisabled={!selectedClient?.clientEmployeeIdEnabled}
                departmentDisabled={!selectedClient?.clientDepartmentEnabled}
                showAvailability
                employeeError={errors.clientEmployeeId?.message}
                departmentError={errors.clientDepartment?.message}
              />
            </fieldset>

            {!companyOwned && (
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <FieldLabel
                    htmlFor="student-tier"
                    label="Tier"
                    help={
                      <>
                      The product entitlement for this private enrollment. An
                      existing enrollment cannot be retiered; moving the
                      student to another tier requires an operator to end and
                      replace it with a new period.
                      </>
                    }
                  />
                  <Controller
                    control={control}
                    name="tier"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange}>
                        <SelectTrigger id="student-tier"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {TIERS.map((tier) => (
                            <SelectItem key={tier} value={tier}>{tier}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  />
                </div>
                <div>
                  <FieldLabel
                    htmlFor="student-program"
                    label="Program"
                    help={
                      <>
                      The program for this enrollment. The student's
                      diagnostic resolves its exact program level.
                      </>
                    }
                  />
                  <Controller
                    control={control}
                    name="programId"
                    render={({ field }) => (
                      <ProgramPicker
                        requireReady
                        id="student-program"
                        value={field.value}
                        onValueChange={field.onChange}
                        invalid={errors.programId !== undefined}
                      />
                    )}
                  />
                  <FieldError message={errors.programId?.message ?? null} />
                </div>
                <div>
                  <FieldLabel
                    htmlFor="student-trophy-goal"
                    label="Trophy goal"
                    help={
                      <>
                      The completion gate for this enrollment. It may support
                      reimbursement, mandatory training, or a personal goal;
                      funding itself is not modeled.
                      </>
                    }
                  />
                  <Input
                    {...trophyGoalField}
                    id="student-trophy-goal"
                    type="number"
                    min="1"
                    step="1"
                    onChange={(event) => {
                      trophyGoalEditedByOperator.current = true
                      trophyGoalField.onChange(event)
                    }}
                  />
                  <FieldError message={errors.trophyGoal?.message ?? null} />
                </div>
                <div>
                  <FieldLabel
                    htmlFor="student-policy-ref"
                    label="External policy reference (optional)"
                    helpLabel="external policy reference"
                    help={
                      <>
                      An opaque CRM, contract, purchase order, or policy
                      identifier. Cooori stores it for traceability but never
                      interprets it.
                      </>
                    }
                  />
                  <Controller
                    control={control}
                    name="externalPolicyRef"
                    render={({ field }) => (
                      <Input id="student-policy-ref" value={field.value} onChange={field.onChange} />
                    )}
                  />
                </div>
              </div>
            )}

            <ProvisioningFormActions
              cancelTo="/admin/students"
              help={
                <p>
                  &ldquo;Create enrollment&rdquo; opens the student afterwards.
                  &ldquo;Create and add another&rdquo; stays here and keeps the
                  client, cohort and tier, so a wave is quick to enter one at a
                  time.
                </p>
              }
            >
              <SecondaryButton
                {...anotherProps}
                pending={submitting}
                disabled={!canSubmit}
              >
                Create and add another
              </SecondaryButton>
              <PrimaryButton
                {...primaryProps}
                pending={submitting}
                pendingLabel="Creating…"
                disabled={!canSubmit}
              >
                Create enrollment
              </PrimaryButton>
            </ProvisioningFormActions>
          </form>

          {lastCreated !== null && (
            // Only on the stay-here path. The primary path navigates to the
            // student, which IS the confirmation.
            <p role="status" className="mt-4 text-body text-text-secondary">
              {keptActivation && 'Existing activation invitation retained with its original send schedule. '}
              Created {lastCreated}. The client, cohort and tier above are
              unchanged.
            </p>
          )}
        </section>
      </div>
    </main>
  )
}
